// frontend/src/pages/admin/SystemAutomationPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, Spin, Typography } from 'antd';
import GeneralSettings from './GeneralSettings';
import EmailGroups from './EmailGroups';
import GlobalNotificationsManagement from './GlobalNotificationsManagement';
import BackupRestoreManagement from './BackupRestoreManagement';
import { useAuth } from '../../context/AuthContext';
import './AdminPanelTabs.css';

const { TabPane } = Tabs;
const { Paragraph } = Typography;

// Placeholder component for Audit Logs
const AuditLogsPlaceholder = () => (
  <div style={{ padding: '20px', textAlign: 'center' }}>
    <h3 style={{ color: 'var(--text-primary)' }}>Audit Logs</h3>
    <Paragraph type="secondary" style={{ color: 'var(--text-secondary)' }}>
      This section will display system audit trails and logs.
      <br />
      (Functionality to be implemented in a future update.)
    </Paragraph>
  </div>
);

const SystemAutomationPage = () => {
  const { 
    user, 
    loading, 
    hasPermission,
    PERMISSION_LEVELS,
    initialized 
  } = useAuth();
  const [activeTabKey, setActiveTabKey] = useState('general-settings');

  // Define tabs with their permission requirements
  const tabs = useMemo(() => [
    {
      key: 'general-settings',
      tab: 'General Settings',
      content: <GeneralSettings />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'email-groups',
      tab: 'Email Groups',
      content: <EmailGroups />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'global-notifications',
      tab: 'Global Notifications',
      content: <GlobalNotificationsManagement />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'backup-restore',
      tab: 'Backup & Restore',
      content: <BackupRestoreManagement />,
      requiredPermission: {
        resource: 'settings',
        action: 'edit',
        level: PERMISSION_LEVELS.ALL
      }
    },
    {
      key: 'audit-logs',
      tab: 'Audit Logs',
      content: <AuditLogsPlaceholder />,
      requiredPermission: {
        resource: 'settings',
        action: 'view',
        level: PERMISSION_LEVELS.ALL
      }
    }
  ], [PERMISSION_LEVELS]);

  // Filter tabs based on permissions
  const accessibleTabs = useMemo(() => {
    if (!initialized || loading || !user) {
      return [];
    }
    return tabs.filter(tab => 
      hasPermission(
        tab.requiredPermission.resource,
        tab.requiredPermission.action,
        tab.requiredPermission.level
      )
    );
  }, [tabs, hasPermission, initialized, loading, user]);

  // Ensure a valid active tab is selected
  useEffect(() => {
    if (initialized && !loading && user && accessibleTabs.length > 0) {
      if (!accessibleTabs.some(tab => tab.key === activeTabKey)) {
        setActiveTabKey(accessibleTabs[0].key);
      }
    }
  }, [activeTabKey, initialized, loading, user, accessibleTabs]);

  // Loading state
  if (!initialized || loading || !user) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <Spin size="large" tip="Loading System & Automation Settings..." />
      </div>
    );
  }

  // No access state
  if (accessibleTabs.length === 0) {
    return (
      <div className="admin-page-container">
        <h1 className="page-title">System & Automation Settings</h1>
        <p className="page-description">You do not have permission to view any system or automation settings tabs.</p>
      </div>
    );
  }

  return (
    <div className="admin-page-container">
      <h1 className="page-title">System & Automation Settings</h1>
      <p className="page-description">
        Manage general application settings and email notification groups.
      </p>

      <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        className="admin-panel-tabs"
      >
        {accessibleTabs.map(tab => (
          <TabPane tab={tab.tab} key={tab.key}>
            {tab.content}
          </TabPane>
        ))}
      </Tabs>
    </div>
  );
};

export default SystemAutomationPage;